import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { useFetchUserQuery } from "../redux/service/modules/auth";
const AuthContext = createContext(null);

const SESSION_TIMEOUT =
    (process.env.REACT_APP_SESSION_TIMEOUT_MINUTES || 120) * 60 * 1000;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [lastActivity, setLastActivity] = useState(Date.now());
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const { data, error, isLoading } = useFetchUserQuery(null, {
        skip: false, // Automatically fetch user info on mount
    });

    useEffect(() => {
        if (data) {
            setUser(data); // Set user data when fetched
            setLoading(false);
        } else if (error) {
            setUser(null); // Clear user data on error
            console.error("Error fetching user info:", error);
            setLoading(false);
        }
    }, [data, error]);

    const login = useCallback(
        (userData) => {
            setUser(userData);
            setLastActivity(Date.now());
            localStorage.setItem("user", JSON.stringify(userData));
            navigate("/dashboard");
        },
        [navigate]
    );

    const logout = useCallback(() => {
        setUser(null);
        setLastActivity(null);
        localStorage.removeItem("user");
        // navigate("/login");
    }, [navigate]);

    const checkSessionTimeout = useCallback(() => {
        if (user && Date.now() - lastActivity > SESSION_TIMEOUT) {
            // logout();
        }
    }, [user, lastActivity, logout]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            checkSessionTimeout();
        }, 60000); // Check every minute

        return () => clearInterval(intervalId);
    }, [checkSessionTimeout]);

    const updateActivity = useCallback(() => {
        setLastActivity(Date.now());
    }, []);

    const refreshToken = async () => {
        try {
            const response = await fetch("/auth/refresh", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ refresh: user?.tokens?.refresh }),
            });
            const data = await response.json();
            if (response.ok) {
                setUser((prevUser) => ({
                    ...prevUser,
                    tokens: {
                        ...prevUser.tokens,
                        access: data.access,
                    },
                }));
                localStorage.setItem(
                    "user",
                    JSON.stringify({
                        ...user,
                        tokens: {
                            ...user.tokens,
                            access: data.access,
                        },
                    })
                );
            } else {
                logout(); // Log out if token refresh fails
            }
        } catch (error) {
            console.error("Token refresh failed:", error);
            logout();
        }
    };

    const value = {
        user,
        login,
        logout,
        loading,
        isAuthenticated: !!user,
        userRole: user?.role,
        checkSessionTimeout,
        refreshToken,
        updateActivity,
    };

    useEffect(() => {
        // Make updateActivity available globally for the API middleware
        window._updateActivity = updateActivity;

        return () => {
            delete window._updateActivity;
        };
    }, [updateActivity]);

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
